import { ChatIcon } from "../ui/icons/Icon";
import { Notice } from "../ui/kit";
import { ListFooter, useServerList, useSort } from "../ui/list";
import { PageState, ServiceState } from "../ui/pieces";
import { useTexts, type Label } from "../texts";
import { availabilityTone, capabilityText, plural, pricePair } from "./ai";
import {
  DataTable,
  FilterRow,
  MoreFilters,
  NoResults,
  Pill,
  PillDivider,
  RowsSkeleton,
  SearchBox,
  TableBar,
  useFilters,
  type SortColumn,
} from "./widgets";
import styles from "./ai.module.css";

type Card = {
  modelId: number;
  name: string;
  providers: string;
  text: boolean;
  images: boolean;
  tools: boolean;
  embedding: boolean;
  /** «Respuestas estructuradas» (viene en la misma consulta de la lista, E106). */
  structured?: boolean;
  minInputPrice: number | null;
  minOutputPrice: number | null;
  priceCurrency: string | null;
  availability: string;
  manualUntested?: boolean;
  inUse: boolean;
  usable: boolean;
};
type ModelItem = { card: Card; code: string; sameNameExists: boolean };

/** Capacidades de una tarjeta en palabras, con la misma función y el mismo orden que el detalle (`capabilityText`). */
export const cardCapabilities = (L: Label, card: Card) =>
  capabilityText(
    L,
    [
      card.text ? "TEXT" : "",
      card.images ? "IMAGE_INPUT" : "",
      card.tools ? "TOOL_CALLING" : "",
      card.structured ? "STRUCTURED_OUTPUT" : "",
      card.embedding ? "EMBEDDING" : "",
    ].join(","),
  );

/** Un modelo en uso sin precio de entrada ni de salida: la columna Precio dice «Sin precio» (C28 1.14.11, E121). */
const missingPrice = (card: Card) => card.inUse && card.minInputPrice == null && card.minOutputPrice == null;

/** Columnas de Modelos que se ordenan en el servidor (C28): el nombre y el precio. */
export const modelSorts = ["name", "price"] as const;

/**
 * La grilla de IA › Modelos (§5.9 `ia-modelos`), una sola pieza para Modelos y para la ficha del proveedor: buscador,
 * Usables · Todos con su conteo, «Más filtros» (Proveedor, Capacidad, Disponibilidad), la tabla C28 paginada y ordenada
 * en el servidor (B3) y, en el celular, la lista C17 con «Ordenar por» en ⋮. Con `scopeProvider` (la ficha) muestra solo
 * los modelos de ese proveedor, también en los conteos de las píldoras, y sin el grupo «Proveedor». Precio sin dato «—»;
 * un modelo en uso sin precio lleva «Sin precio». El identificador solo cuando distingue dos modelos con el mismo nombre
 * (D7). Todo vive en la dirección (E84).
 */
export function ModelsGrid({ scopeProvider }: { scopeProvider?: string }) {
  const { L, locale } = useTexts();
  const { get, set } = useFilters();
  const order = useSort(modelSorts);
  const scope = get("scope") === "all" ? "all" : "";
  const provider = scopeProvider ? "" : get("provider");
  const capability = get("capability");
  const availability = get("availability");
  const search = get("search");
  const query = new URLSearchParams(
    Object.entries({ scope, provider, scopeProvider: scopeProvider ?? "", capability, availability, search }).filter(
      ([, value]) => value,
    ) as [string, string][],
  ).toString();
  const url = `/api/admin/ai/models?${[query, order.query].filter(Boolean).join("&")}`.replace(/\?$/, "");
  const list = useServerList<ModelItem>(url);
  const counts = (list.extra?.counts as { usable: number; all: number } | undefined) ?? null;
  const providers = (list.extra?.providers as { code: string; name: string }[] | undefined) ?? [];
  if (list.state === "forbidden") return <PageState title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;

  const capabilityLabel: Record<string, string> = {
    TEXT: L("MODELS_CAP_TEXT"),
    EMBEDDING: L("MODELS_CAP_EMBEDDING"),
    IMAGE_INPUT: L("MODELS_CAP_IMAGES"),
  };
  const availabilityLabel: Record<string, string> = {
    AVAILABLE: L("ADM_AI_AVAILABLE"),
    UNAVAILABLE: L("ADM_AI_UNAVAILABLE"),
    UNKNOWN: L("ADM_AI_UNVERIFIED"),
  };
  const providerName = providers.find((item) => item.code === provider)?.name ?? provider;
  const applied = [
    provider ? { key: "provider", label: providerName } : null,
    capability ? { key: "capability", label: capabilityLabel[capability] ?? capability } : null,
    availability ? { key: "availability", label: availabilityLabel[availability] ?? availability } : null,
  ].filter(Boolean) as { key: string; label: string }[];
  const filtered = !!(provider || capability || availability || search);
  // «En uso» junto al nombre siempre que el modelo está en uso; «Sin precio» va en la columna Precio, no la tapa (E121).
  const tag = (item: ModelItem) =>
    item.card.inUse ? (
      <span className={styles.tagAttention} data-part="en-uso">
        {L("ADM_AI_IN_USE")}
      </span>
    ) : null;
  const price = (card: Card) => pricePair(locale, card.minInputPrice, card.minOutputPrice, card.priceCurrency);
  const noPrice = (
    <span className={styles.noPrice} data-part="precio">
      {L("ADM_AI_NO_PRICE_CAP")}
    </span>
  );
  const sortColumns: SortColumn[] = [
    { key: "name", title: L("ADM_AI_COL_MODEL"), kind: "text" },
    { key: "price", title: L("ADM_AI_COL_PRICE"), kind: "number" },
  ];
  const groups = [
    ...(scopeProvider
      ? []
      : [
          {
            key: "provider",
            title: L("ADM_AI_PROVIDER"),
            options: providers.map((item) => ({ value: item.code, label: item.name })),
          },
        ]),
    {
      key: "capability",
      title: L("ADM_AI_CAPABILITY"),
      options: Object.entries(capabilityLabel).map(([value, label]) => ({ value, label })),
    },
    {
      key: "availability",
      title: L("ADM_AI_AVAILABILITY"),
      options: Object.entries(availabilityLabel).map(([value, label]) => ({ value, label })),
    },
  ];

  return (
    <>
      <div className={styles.toolbar}>
        <SearchBox
          id="models-search"
          value={search}
          placeholder={L(scopeProvider ? "ADM_AI_SEARCH_MODEL_ONLY" : "ADM_AI_SEARCH_MODEL")}
          onChange={(value) => set({ search: value })}
        />
        <FilterRow label={L("ADM_AI_FILTERS")}>
          <Pill pressed={scope === ""} onClick={() => set({ scope: "" })}>
            {L("ADM_AI_USABLE")}
            {counts ? ` ${counts.usable.toLocaleString(locale)}` : null}
          </Pill>
          <Pill pressed={scope === "all"} onClick={() => set({ scope: "all" })}>
            {L("ADM_AI_ALL")}
            {counts ? ` ${counts.all.toLocaleString(locale)}` : null}
          </Pill>
          {applied.map((item) => (
            <Pill
              key={item.key}
              pressed
              onClick={() => set({ [item.key]: "" })}
              label={`${L("ADM_AI_REMOVE_FILTER")}: ${item.label}`}
            >
              {item.label}
              <ChatIcon name="x" />
            </Pill>
          ))}
          <PillDivider />
          <MoreFilters
            groups={groups}
            value={{ provider, capability, availability }}
            onApply={(next) =>
              set({
                ...(scopeProvider ? {} : { provider: next.provider ?? "" }),
                capability: next.capability ?? "",
                availability: next.availability ?? "",
              })
            }
          />
        </FilterRow>
      </div>
      {list.state === "loading" ? (
        <RowsSkeleton rows={6} table />
      ) : list.state === "error" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults
            query={search}
            things={L("ADM_AI_MODELS_THINGS")}
            onClear={() => set(search ? { search: "" } : { provider: "", capability: "", availability: "" })}
          />
        ) : (
          <div className="tm-empty" role="status">
            <strong>{L(scope ? "ADM_AI_NO_MODELS" : "ADM_AI_NO_USABLE")}</strong>
            <span>{L(scope ? "ADM_AI_NO_MODELS_LINE" : "ADM_AI_NO_USABLE_LINE")}</span>
            {scope ? (
              <a className="tm-btn secondary" href="/Ai/Avanzado/Manual">
                {L("ADM_AI_MANUAL_TITLE")}
              </a>
            ) : (
              <button type="button" className="tm-btn secondary" onClick={() => set({ scope: "all" })}>
                {L("ADM_AI_SEE_ALL")}
              </button>
            )}
          </div>
        )
      ) : (
        <>
          <TableBar
            count={plural(L, locale, list.total, "ADM_N_MODELS_ONE", "ADM_N_MODELS")}
            columns={sortColumns}
            sort={order.sort}
            onSort={order.set}
          />
          <DataTable<ModelItem>
            label={L("ADM_AI_MODELS")}
            rows={list.items}
            rowKey={(item) => item.card.modelId}
            href={(item) => `/Ai/Modelos/Detail/${item.card.modelId}`}
            sort={order.sort}
            onSort={order.set}
            columns={[
              {
                key: "model",
                title: L("ADM_AI_COL_MODEL"),
                sort: { key: "name", kind: "text" },
                width: 272,
                render: (item) => (
                  <span className={styles.cellMain}>
                    {/* Una línea: el nombre termina en «…» y se lee entero al pasar el mouse; «En uso» no baja (H3). */}
                    <span className={styles.modelCell}>
                      <strong title={item.card.name}>{item.card.name}</strong>
                      {tag(item)}
                    </span>
                    {item.sameNameExists ? <span className={styles.cellSub}>{item.code}</span> : null}
                  </span>
                ),
              },
              // En la ficha de un proveedor la columna «Proveedores» repetiría su nombre en cada fila (M20): no va.
              ...(scopeProvider
                ? []
                : [
                    {
                      key: "providers",
                      title: L("ADM_AI_COL_PROVIDERS"),
                      width: 100,
                      clip: true,
                      render: (item: ModelItem) => item.card.providers || "—",
                    },
                  ]),
              {
                key: "caps",
                title: L("ADM_AI_CAPABILITIES"),
                clip: true,
                render: (item) => {
                  const caps = cardCapabilities(L, item.card);
                  return <span title={caps}>{caps}</span>;
                },
              },
              {
                key: "availability",
                title: L("ADM_AI_AVAILABILITY"),
                width: 134,
                render: (item) => {
                  const tone = availabilityTone(L, item.card.availability);
                  return <ServiceState tone={tone.tone}>{tone.text}</ServiceState>;
                },
              },
              {
                key: "price",
                title: L("ADM_AI_COL_PRICE"),
                numeric: true,
                width: 223,
                sort: { key: "price", kind: "number" },
                render: (item) =>
                  missingPrice(item.card) ? noPrice : <span data-part="precio">{price(item.card)}</span>,
              },
            ]}
            mobile={(item) => {
              // Sin datos no se escribe «— · —» (M20): solo lo que hay; «Sin precio» en ocre en vez del precio.
              const text = [
                scopeProvider ? null : item.card.providers || null,
                cardCapabilities(L, item.card),
                missingPrice(item.card) ? null : price(item.card),
              ]
                .filter((part) => part && part !== "—")
                .join(" · ");
              return {
                title: item.card.name,
                tag: tag(item),
                // «Sin precio» nunca se recorta: lo que va antes cede el espacio y termina en «…».
                line: missingPrice(item.card) ? (
                  <span className={styles.lineKeepEnd}>
                    {text ? <span>{text}</span> : null}
                    <span>
                      {text ? " · " : null}
                      {noPrice}
                    </span>
                  </span>
                ) : (
                  text || "—"
                ),
              };
            }}
          />
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}
